import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';
import classnames from 'classnames';

import { FormattedMessage } from 'react-intl';
import { SwatchSelectorMinimal } from 'dibs-swatch-selector/exports/SwatchSelectorMinimal';
import { TileContextProvider } from '../helpers/TileContext';
import { Product } from '../Product';
import { Price } from '../Price';
import { ProductTileMeasurements } from './ProductTileMeasurements';
import { QuickViewButton } from '../QuickViewButton';
import { VisibilityTracker } from '../VisibilityTracker/VisibilityTracker';
import { ShippingBadge } from '../Badges/Shipping';
import QuickViewItem from '../QuickViewItem';
import OfferedBy from '../Info/OfferedBy';
import InternalSellerName from '../Info/InternalSellerName';

import styles from './TileTestVariant.scss';

const CREATOR_ATTRIBUTED_BY = 'BY';
const CREATOR_ATTRIBUTED_BY_AND_DOCUMENTED = 'BY_AND_DOCUMENTED';
const CREATOR_ATTRIBUTION_LIST = [CREATOR_ATTRIBUTED_BY, CREATOR_ATTRIBUTED_BY_AND_DOCUMENTED];

export function TileTestVariantComponent({
    item,
    index,
    imageSize,
    currency,
    priceBold,
    showPrice,
    showBrowseSimilar,
    showMeasurements,
    showMSKUSwatch,
    showHoverState,
    showCreatorName,
    showQuickViewIcon,
    showInternalSellerName,
    showSellerName,
    isMobile,
    measurementUnit,
    itemsPerRow,
    onClick,
    onContentLoaded,
    onContentVisible,
    onQuickViewDetailsClick,
    openQuickView,
    useLoFiLazyLoader,
    imageLoadVerticalOffset,
    activeQuickView,
    closeQuickView,
    hideImagePadding,
    srcSetSizes,
    srcSetWidths,
    srcSetQuality,
    favoritesComponent,
    viewer,
}) {
    const tileRef = useRef(null);

    const title = get(item, 'title');
    const localizedPdpUrl = get(item, 'localizedPdpUrl');
    const isArt = get(item, 'isArt');
    const creators = get(item, 'creators') || [];
    const itemId = get(item, 'serviceId');
    const similarItemsHref = `/similar/?item=${itemId}`;
    const attribution = creators.find(el => CREATOR_ATTRIBUTION_LIST.includes(el.attribution));
    const attributionCreatorName = get(attribution, 'creator.displayName');
    const creationDate = get(item, 'classification.creationDate') || null;
    const sellerName = get(item, 'seller.sellerProfile.company');
    const showTileFooter = !isMobile && (showBrowseSimilar || showMeasurements);
    const shouldShowInternalSellerName = showInternalSellerName && !!sellerName;
    const shouldShowSellerName = !showCreatorName && showSellerName && !!sellerName;
    const isQuickViewActive = index === activeQuickView;

    const containerClassNames = classnames(styles.container, {
        [styles.activeHover]: showHoverState || isQuickViewActive,
    });
    const footerClassNames = classnames(styles.footer, {
        [styles.activeHover]: showHoverState,
    });

    const openQuickViewFunc = () => openQuickView(index);

    const titleEl = (
        <a
            data-tn="portfolio-row-creator-name"
            onClick={onClick}
            className={classnames(styles.title, styles.tileLink, styles.titleLink)}
            href={localizedPdpUrl}
        >
            {isArt ? (
                <React.Fragment>
                    {attributionCreatorName && (
                        <div
                            className={classnames(styles.ellipsis, styles.creator)}
                            data-tn="portfolio-row-creator-name"
                        >
                            {attributionCreatorName}
                        </div>
                    )}
                    <div className={styles.artTitle}>
                        <span className={styles.ellipsis} data-tn="portfolio-row-product-name">
                            {title}
                        </span>
                        {creationDate && `, ${creationDate}`}
                    </div>
                </React.Fragment>
            ) : (
                title
            )}
        </a>
    );

    return (
        <TileContextProvider imageSize={imageSize}>
            <div className={containerClassNames} ref={tileRef}>
                {shouldShowInternalSellerName && (
                    <InternalSellerName isTestVariant sellerName={sellerName} />
                )}
                {favoritesComponent}
                {onContentVisible && (
                    <VisibilityTracker elementRef={tileRef} onContentVisible={onContentVisible} />
                )}
                <QuickViewItem
                    isTestVariant
                    item={item}
                    isMobile={isMobile}
                    isQuickViewActive={isQuickViewActive}
                    onClose={closeQuickView}
                    onViewDetailsClick={onQuickViewDetailsClick}
                />
                <div className={styles.content}>
                    <Product
                        hideProductDetails
                        hideDivider
                        index={index}
                        item={item}
                        onClick={onClick}
                        title={title}
                        pdpUrl={localizedPdpUrl}
                        itemsPerRow={itemsPerRow}
                        onContentLoaded={onContentLoaded}
                        useLoFiLazyLoader={useLoFiLazyLoader}
                        imageLoadVerticalOffset={imageLoadVerticalOffset}
                        hideImagePadding={hideImagePadding}
                        srcSetSizes={srcSetSizes}
                        srcSetWidths={srcSetWidths}
                        srcSetQuality={srcSetQuality}
                    />
                    <div className={styles.info}>
                        {isMobile && showMeasurements && (
                            <div className={`${styles.measurementsWrapper} ${styles.ellipsis}`}>
                                <ProductTileMeasurements
                                    item={item}
                                    measurementUnit={measurementUnit}
                                    isMobile={isMobile}
                                />
                            </div>
                        )}
                        <div className={styles.titleWrapper}>
                            {titleEl}
                            <QuickViewButton
                                isTestVariant
                                openQuickView={openQuickViewFunc}
                                showQuickViewIcon={showQuickViewIcon}
                                hideDivider
                            />
                        </div>
                        {showPrice ? (
                            <Price
                                isTestVariant
                                currency={currency}
                                item={item}
                                priceBold={priceBold}
                            />
                        ) : (
                            <div className={styles.pricePlaceHolder} /> // placeholder to avoid flicker when when we know currency
                        )}
                        <ShippingBadge item={item} viewer={viewer} />
                        {showMSKUSwatch && (
                            <a className={styles.tileLink} href={localizedPdpUrl} onClick={onClick}>
                                <SwatchSelectorMinimal
                                    displayCount={isMobile ? 3 : 4}
                                    item={item}
                                />
                            </a>
                        )}
                        {shouldShowSellerName && (
                            <OfferedBy className={styles.offeredBy} sellerName={sellerName} />
                        )}
                        {showTileFooter && (
                            <div className={footerClassNames}>
                                <div>
                                    {showMeasurements && (
                                        <ProductTileMeasurements
                                            item={item}
                                            measurementUnit={measurementUnit}
                                        />
                                    )}
                                </div>
                                {showBrowseSimilar && (
                                    <FormattedMessage
                                        id="dc.searchProductTile.browseSimilar"
                                        defaultMessage="Browse Similar"
                                    >
                                        {msg => <a href={similarItemsHref}>{msg}</a>}
                                    </FormattedMessage>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </TileContextProvider>
    );
}

TileTestVariantComponent.propTypes = {
    item: PropTypes.object,
    viewer: PropTypes.object,
    index: PropTypes.number,
    imageSize: PropTypes.string,
    currency: PropTypes.string,
    showBrowseSimilar: PropTypes.bool,
    isMobile: PropTypes.bool,
    showPrice: PropTypes.bool,
    showByLine: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    showMSKUSwatch: PropTypes.bool,
    showHoverState: PropTypes.bool,
    showSellerName: PropTypes.bool,
    priceBold: PropTypes.bool,
    measurementUnit: PropTypes.string,
    itemsPerRow: PropTypes.number,
    showQuickView: PropTypes.bool,
    useLoFiLazyLoader: PropTypes.bool,
    imageLoadVerticalOffset: PropTypes.number,
    showQuickViewIcon: PropTypes.bool,
    showInternalSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    hideImagePadding: PropTypes.bool,
    srcSetSizes: PropTypes.string,
    srcSetWidths: PropTypes.string,
    srcSetQuality: PropTypes.string,
    favoritesComponent: PropTypes.node,
    activeQuickView: PropTypes.number,
    onClick: PropTypes.func,
    onContentLoaded: PropTypes.func,
    onContentVisible: PropTypes.func,
    onQuickViewDetailsClick: PropTypes.func,
    openQuickView: PropTypes.func,
    closeQuickView: PropTypes.func,
};

TileTestVariantComponent.defaultProps = {
    imageSize: 'medium',
    currency: 'USD',
    disableLinks: false,
    itemsPerRow: 3,
    useLoFiLazyLoader: false,
    priceBold: false,
    measurementUnit: 'IN',
    showByLine: true,
    showQuickView: true,
    showQuickViewIcon: true,
    showLightBox: false,
    showPrice: true,
    showMeasurements: true,
    showMSKUSwatch: true,
    showHoverState: false,
    showSellerName: false,
    onQuickViewDetailsClick: () => {},
    viewer: null,
};

export const TileTestVariant = createFragmentContainer(TileTestVariantComponent, {
    viewer: graphql`
        fragment TileTestVariant_viewer on Viewer
            @argumentDefinitions(
                fetchRegionalInfo: { type: "Boolean", defaultValue: false }
                userZipCode: { type: "String", defaultValue: "0" }
                userCountryCode: { type: "String", defaultValue: "" }
            ) {
            ...Shipping_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
        }
    `,
    item: graphql`
        fragment TileTestVariant_item on Item
            @argumentDefinitions(
                isTrade: { type: "Boolean", defaultValue: false }
                showSeller: { type: "Boolean", defaultValue: false }
                pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
            ) {
            serviceId
            title
            localizedPdpUrl
            classification {
                creationDate
            }
            creators {
                attribution
                creator {
                    displayName
                }
            }
            isArt
            seller @include(if: $showSeller) {
                serviceId
                sellerProfile {
                    company
                }
            }
            ...Shipping_item
            ...SwatchSelectorMinimal_item
            ...Product_item
            ...Price_item @arguments(isTrade: $isTrade, pageDisplayEnum: $pageDisplayEnum)
            ...QuickViewItem_item
            ...ProductTileMeasurements_item
        }
    `,
});
